<template>
  <div class="login-page">
    <!-- Left Side — Branding -->
    <div class="login-left">
      <div class="login-left-content">
        <img src="/logo.svg" alt="ForMind" class="brand-logo" />

        <h1>{{ t('auth.welcome') }}</h1>
        <p class="brand-desc">
          {{ t('auth.brandDescription') }}
        </p>

        <div class="brand-badges">
          <span class="badge"><span class="badge-icon"><Zap :size="14" /></span> {{ t('auth.badgeAI') }}</span>
          <span class="badge"><span class="badge-icon"><BarChart3 :size="14" /></span> {{ t('auth.badgeRealtime') }}</span>
          <span class="badge"><span class="badge-icon"><Lock :size="14" /></span> {{ t('auth.badgeSecure') }}</span>
        </div>
      </div>
    </div>

    <!-- Right Side — Login Form -->
    <div class="login-right">
      <div class="login-form-wrapper">
        <h2>{{ t('auth.loginTitle') }}</h2>
        <p class="form-subtitle">{{ t('auth.loginSubtitle') }}</p>

        <form @submit.prevent="handleLogin" class="login-form">
          <div class="field">
            <label>{{ t('common.username') }}</label>
            <input
              type="text"
              v-model="username"
              :placeholder="t('common.username')"
              required
              :disabled="loading"
              autocomplete="username"
            />
          </div>

          <div class="field">
            <label>{{ t('common.password') }}</label>
            <input
              type="password"
              v-model="password"
              :placeholder="t('common.password')"
              required
              :disabled="loading"
              autocomplete="current-password"
            />
          </div>

          <button type="submit" class="login-btn" :disabled="loading">
            <span v-if="!loading">{{ t('common.login') }}</span>
            <span v-else class="loading-state">
              <span class="spinner"></span>
              {{ t('auth.loggingIn') }}
            </span>
          </button>

          <Transition name="error">
            <div v-if="error" class="error-msg" role="alert" aria-live="assertive">
              <AlertCircle :size="18" aria-hidden="true" />
              <span>{{ error }}</span>
            </div>
          </Transition>
        </form>

        <div class="login-footer">Powered by <strong>ForInvest</strong></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { getRecaptchaToken, hideRecaptchaBadge, loadRecaptcha, showRecaptchaBadge } from '@/utils/recaptcha'
import { AlertCircle, Zap, BarChart3, Lock } from 'lucide-vue-next'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()

const username = ref('')
const password = ref('')
const loading = ref(false)
const error = ref('')

onMounted(() => {
  showRecaptchaBadge()
  loadRecaptcha().catch(() => {})
})

router.beforeEach(() => {
  hideRecaptchaBadge()
})

async function handleLogin() {
  if (!username.value || !password.value) {
    error.value = t('auth.usernameRequired')
    return
  }

  loading.value = true
  error.value = ''

  try {
    const recaptchaToken = await getRecaptchaToken('login')
    const result = await authStore.login(username.value, password.value, recaptchaToken)

    if (result.success) {
      await router.push('/chat')
    } else {
      error.value = result.error || t('auth.loginFailed')
    }
  } catch (err: unknown) {
    error.value = err instanceof Error ? err.message : t('auth.loginError')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped src="@/assets/styles/views/LoginView.css"></style>
